import { Fragment, ReactNode } from "react";

/**
 * Trademark and copyright symbols that are set as superscript everywhere on the
 * site, e.g. Invisalign®, Aesthetic Concept©.
 */
const SYMBOLS = /([©®™])/;

/**
 * Splits a plain string and wraps every ©, ® and ™ in a <sup>, so the symbols
 * are typeset consistently regardless of which field or component renders the
 * text. Strings without a symbol are returned unchanged.
 *
 * Use this only for text that is rendered into the page. Anything that must
 * stay plain text (<title>, meta tags, alt attributes, JSON-LD) should keep
 * the raw string.
 */
export const superscriptSymbols = (text?: ReactNode): ReactNode => {
  // Accepts any ReactNode so call sites don't have to narrow; only plain
  // strings are processed, elements pass through untouched.
  if (typeof text !== "string" || !SYMBOLS.test(text)) return text;

  return text.split(SYMBOLS).map((part, i) =>
    SYMBOLS.test(part) ? (
      // Preflight's sup defaults (75%, top -0.5em) are too large next to
      // headings, so the symbol is scaled down and lifted to cap height.
      <sup key={i} className="relative -top-[0.5em] align-baseline text-[0.6em] leading-[0]">
        {part}
      </sup>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
};
